import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '../../services/toastService'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import AdminHeader from '../../components/admin/AdminHeader'
import UserDetailHeader from '../../components/admin/UserDetailHeader'
import UserInfoDisplay from '../../components/admin/UserInfoDisplay'
import UserEditForm from '../../components/admin/UserEditForm'
import UserMetadataView from '../../components/admin/UserMetadataView'
import { createLogger } from '../../utils/logger'
import { formatDateTime } from '../../utils/dateFormatting'

const logger = createLogger('UserDetail')

/**
 * UserDetail - Admin user management page
 * Orchestrates user data fetching, editing, and deletion
 * Optimized from 360 lines by extracting display components
 */
export default function UserDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading, error, execute } = useAsyncOperation()
  const isEditMode = id && id !== 'new'
  const [user, setUser] = useState(null)
  const [editing, setEditing] = useState(!isEditMode)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    isAdmin: false,
    isVerified: false
  })

  useEffect(() => {
    if (isEditMode) {
      loadUser()
    }
  }, [id])

  const loadUser = async () => {
    await execute(
      async () => {
        const response = await adminAPI.getUser(id)
        const userData = response.data
        setUser(userData)
        setFormData({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          isActive: userData.isActive,
          isAdmin: userData.isAdmin,
          isVerified: userData.isVerified
        })
        return userData
      },
      { errorMessage: 'Failed to load user details' }
    )
  }

  const handleSave = async () => {
    await execute(async () => {
      if (isEditMode) {
        await adminAPI.updateUser(id, formData)
        toast.success(t('toast.success.userUpdated'))
        setEditing(false)
        loadUser()
      } else {
        await adminAPI.createUser(formData)
        toast.success('User created successfully')
        navigate('/admin/users')
      }
    }, { errorMessage: isEditMode ? 'Failed to update user' : 'Failed to create user' })
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      await adminAPI.deleteUser(id)
      toast.success('User deleted successfully')
      navigate('/admin/users')
    } catch (error) {
      logger.error('Failed to delete user', error)
      toast.error('Failed to delete user')
    }
  }

  const handleCancel = () => {
    if (isEditMode) {
      setEditing(false)
      setFormData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        isActive: user?.isActive ?? true,
        isAdmin: user?.isAdmin ?? false,
        isVerified: user?.isVerified ?? false
      })
    } else {
      navigate('/admin/users')
    }
  }

  if (loading && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('app.loading')}</div>
      </div>
    )
  }

  if (!user && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">User not found</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="text-primary hover:text-primary-600"
          >
            {t('admin.backToCollection')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <UserDetailHeader
            isEditMode={isEditMode}
            editing={editing}
            onBack={() => navigate('/admin/users')}
            onEdit={() => setEditing(true)}
            onDelete={handleDelete}
          onSave={handleSave}
          onCancel={handleCancel}
          t={t}
        />

        {/* User Info Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="p-6">
            {isEditMode && user && !editing && (
              <UserInfoDisplay user={user} />
            )}

            {editing ? (
              <UserEditForm
                formData={formData}
                onChange={setFormData}
              />
            ) : (
              <UserMetadataView user={user} />
            )}
          </div>
        </div>

        {/* Additional Info - Only show in edit mode */}
        {isEditMode && user && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">User ID</div>
              <div className="mt-1 font-mono text-xs text-gray-900 break-all">{user.id}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Created</div>
              <div className="mt-1 text-sm text-gray-900">
                {formatDateTime(user.createdAt)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Updated</div>
              <div className="mt-1 text-sm text-gray-900">
                {formatDateTime(user.updatedAt)}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}
